import type { CheckboxProps as MuiCheckboxProps } from "@mui/material";
import {
  Checkbox as MuiCheckbox,
  FormControlLabel as MuiFormControlLabel,
} from "@mui/material";
import type { FormikValues } from "formik";
import { useField, useFormikContext } from "formik";
import type { ReactNode } from "react";
import { forwardRef, useCallback, useImperativeHandle } from "react";

import type { DataFullstoryId } from "@citrine/types/full-story";
import { sleep } from "@citrine/utils/helpers";
import type { Paths } from "@citrine/utils/types";

import { useFormConfiguration } from ".";
import { useValidateOnChange } from "./utils";

type SupportedValues = string[] | boolean | undefined;

interface CheckboxRef<Value extends SupportedValues> {
  setValue: (value: Value, shouldValidate?: boolean) => void;
}

type CheckboxProps<
  Shape extends FormikValues,
  Value extends SupportedValues,
> = Omit<MuiCheckboxProps, "name"> & {
  "data-fullstoryid"?: DataFullstoryId;
  label?: ReactNode;
  validateOnChange?: boolean;
} & (Value extends (infer ScalarValue extends string)[]
    ? {
        // If `name` references a string[] field, then `value` is the string
        // value that will be added or removed from the array when the checkbox
        // is toggled.
        name: Paths<Shape, Value>;
        value: ScalarValue;
      }
    : {
        name: Paths<Shape, Value>;
        value?: undefined;
      });

function CheckboxImpl<
  Shape extends FormikValues,
  Value extends SupportedValues,
>(
  {
    name,
    disabled,
    label,
    onChange,
    value,
    validateOnChange: validateOnChangeProp,
    "data-fullstoryid": fullstoryId,
    ...props
  }: CheckboxProps<Shape, Value>,
  ref: React.ForwardedRef<CheckboxRef<Value>>,
) {
  const [field, , helpers] = useField({
    name,
    value,
    type: "checkbox",
  });
  const { isSubmitting, validateForm } = useFormikContext();
  const { readonly } = useFormConfiguration();

  const validateOnChange = useValidateOnChange(validateOnChangeProp);

  useImperativeHandle(
    ref,
    () => ({
      setValue: helpers.setValue,
    }),
    [helpers.setValue],
  );

  const onChangeCallback = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
      onChange?.(event, checked);
      field.onChange(event);
      if (validateOnChange) {
        // perform this async to allow all form changes to propagate
        await sleep(0);
        validateForm();
      }
    },
    [field, onChange, validateForm, validateOnChange],
  );

  return (
    <MuiFormControlLabel
      data-fullstoryid={fullstoryId}
      label={label}
      control={
        <MuiCheckbox
          {...props}
          {...field}
          // If field.value is undefined, when a change event fires, `event.target.value`
          // ends up being ["on"] or [] instead of true/false.
          value={field.value ?? false}
          onChange={onChangeCallback}
          disabled={disabled || readonly || isSubmitting}
        />
      }
    />
  );
}

export const Checkbox = forwardRef(CheckboxImpl) as <
  Shape extends FormikValues = FormikValues,
  Value extends SupportedValues = boolean,
>(
  props: CheckboxProps<Shape, Value> & {
    ref?: React.ForwardedRef<CheckboxRef<Value>>;
  },
) => ReturnType<typeof CheckboxImpl>;
